import { Result, Skeleton } from "@arco-design/web-react";
import { type ReactNode, useCallback, useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { canUsePermission } from "../../../lib/auth";
import { HTTP_AUTH_EXPIRED_EVENT } from "../../../lib/http/axios";
import { isHttpClientError } from "../../../lib/http/errors";
import { buildAuthReturnPath } from "../service/authReturnPath";
import { useCurrentAuthUser } from "./useCurrentAuthUser";

/** 对直接 URL 访问执行统一页面权限守卫。 */
export function PermissionRoute({ permission, children }: { permission: string; children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentUserQuery = useCurrentAuthUser();
  const redirectToExpiredLogin = useCallback(() => {
    navigate("/auth/login?reason=expired", {
      replace: true,
      state: { from: buildAuthReturnPath(location) },
    });
  }, [location, navigate]);

  useEffect(() => {
    window.addEventListener(HTTP_AUTH_EXPIRED_EVENT, redirectToExpiredLogin);
    return () => {
      window.removeEventListener(HTTP_AUTH_EXPIRED_EVENT, redirectToExpiredLogin);
    };
  }, [redirectToExpiredLogin]);

  if (currentUserQuery.isPending || currentUserQuery.isLoading) {
    return <Skeleton text={{ rows: 6 }} animation />;
  }
  if (
    currentUserQuery.isError
    && isHttpClientError(currentUserQuery.error)
    && currentUserQuery.error.statusCode === 401
  ) {
    return (
      <Navigate
        to="/auth/login?reason=expired"
        replace
        state={{ from: buildAuthReturnPath(location) }}
      />
    );
  }
  if (
    !currentUserQuery.isSuccess
    || !canUsePermission(currentUserQuery.data, permission)
  ) {
    return <Result status="403" title="无权访问" subTitle="当前账号未获得此页面权限，请联系管理员。" />;
  }
  return children;
}
